'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var apisauce = require('apisauce');
var userLibrary = require('@tiller/user-library');

/**
 * This class is responsible of creating a Response.
 *
 * @class Response
 */
class Response {
  /**
   * Create a new instance of Response.
   *
   * @param {boolean} ok
   * @param {mixed} data - Body content of the response
   *
   * @return {Response}
   */
  constructor(ok, data) {
    this.ok = ok;
    this.data = data;
  }
  /**
   * Return whether the response is successful or not
   *
   * @return {boolean}
   */


  isSuccess() {
    return this.ok;
  }
  /**
   * Return the body content of the response
   *
   * @return {mixed} data
   */


  getContent() {
    return this.data;
  }
  /**
   * Return the error code
   *
   * @return {string} email
   */


  getErrorCode() {
    return this.data.code;
  }
  /**
   * Return the error message
   *
   * @return {string} email
   */


  getErrorMessage() {
    return this.data.message;
  }
  /**
   * Serializes the Response.
   *
   * @return {Object} The serialized object.
   */


  serialize() {
    return { ...this
    };
  }

}

/**
 * Gets the pre-request transform.
 *
 * @param {function} getToken - method to get the token.
 *
 * @return {function}
 */

const getPreRequestTransform = getToken => request => {
  request.headers['Authorization'] = `Bearer ${getToken()}`;
};
/**
 * This class implements the user manager that is an interface to the API.
 */

class ApiUserManager {
  /**
   * Creates a new instance of the manager.
   *
   * @param {string} baseURL - The base URL of the API.
   * @param {function} getToken - A function that retrieve a token.
   *
   * @return {ApiUserManager}
   *
   * @throws {Error} - If no base URL is given.
   * @throws {Error} - If no token is given.
   */
  constructor(baseURL, getToken) {
    if (!baseURL) {
      throw new Error('Missing base URL.');
    }

    if (!getToken) {
      throw new Error('Missing token.');
    }

    this.api = apisauce.create({
      baseURL
    });
    this.api.addRequestTransform(getPreRequestTransform(getToken));
  }
  /**
   * Get current User information
   *
   * @async
   *
   * @return {Response} a new instance of Response.
   */


  async getUserInfo() {
    const {
      ok,
      data
    } = await this.api.get(`/user`);
    return new Response(ok, ok ? userLibrary.User.deserialize(data) : data);
  }
  /**
   * Find an User by provided email.
   *
   * @param {string} email - provided email.
   *
   * @return {Response} a new instance of Response.
   */


  async findUserByEmail(email) {
    const {
      ok,
      data
    } = await this.api.get(`/user?email=${encodeURIComponent(email)}`);
    return new Response(ok, ok ? userLibrary.User.deserialize(data) : data);
  }
  /**
   * Get current User sidebar links
   *
   * @async
   *
   * @return {Response} a new instance of Response.
   */


  async getUserSidebar() {
    const {
      ok,
      data
    } = await this.api.get(`/sidebar`);
    return new Response(ok, data);
  }
  /**
   * Create an User.
   *
   * @async
   *
   * @param {Object} userData - Javascript object containing user data.
   *
   * @return {Response} a new instance of Response.
   */


  async createUser(userData) {
    const {
      ok,
      data
    } = await this.api.post('/users', userData);
    return new Response(ok, ok ? userLibrary.User.deserialize(data) : data);
  }
  /**
   * Update an User.
   *
   * @async
   *
   * @param {Object} userData - Javascript object containing user data to update.
   *
   * @return {Response} a new instance of Response.
   */


  async updateUser(userData) {
    const {
      ok,
      data
    } = await this.api.patch('/user', userData);
    return new Response(ok, data);
  }
  /**
   * Assign an User to a Store.
   *
   * @async
   *
   * @param {Object} assigmentData - data for assigment. StoreId is required. UserId is optionnal.
   *
   * @return {Response} a new instance of Response.
   */


  async assignUser(assigmentData) {
    if (!assigmentData.storeId) throw new Error('field storeId is required.');
    const {
      ok,
      data
    } = await this.api.post('/manager/assign', assigmentData);
    return new Response(ok, data);
  }
  /**
   * Revoke an User to a Store.
   *
   * @async
   *
   * @param {Object} revokeData - data for revokation. UserId is optionnal.
   *
   * @return {Response} a new instance of Response.
   */


  async revokeUser(revokeData) {
    if (!revokeData.storeId) throw new Error('field storeId is required.');
    const {
      ok,
      data
    } = await this.api.post('/manager/revoke', revokeData);
    return new Response(ok, data);
  }
  /**
   * Create an User.
   *
   * @async
   *
   * @param {Object} userData - Javascript object containing user data.
   *
   * @return {Response} a new instance of Response.
   */


  async requestConfirmationToken(id) {
    const {
      ok,
      data
    } = await this.api.post('/user/password/token', {
      id
    });
    return new Response(ok, data);
  }

}

/**
 * Initializes the SDK.
 *
 * @param {string} baseURL - The base URL of the API.
 * @param {function} getToken - A function that retrieves a token.
 *
 * @return {ApiUserManager} An instance of user manager.
 */

async function init({
  baseURL,
  getToken
}) {
  return new ApiUserManager(baseURL, getToken);
}

exports.init = init;
